import React, { useCallback, useEffect, useState } from "react";
import { MagnifyingGlass, Receipt, X } from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Spinner } from "components/ui/spinner";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";

interface RenoTicketOption {
  id: number;
  title: string;
  status: string;
  buildingId?: string | null;
  renovationProject?: { id: number; title: string } | null;
}

interface Props {
  /** Document dat aan een renovatieticket gekoppeld wordt; `null` sluit de modal. */
  document: {
    id: number;
    documentNumber: string;
    renoTicketId?: number | null;
  } | null;
  onClose: () => void;
  /** Aangeroepen na een geslaagde (ont)koppeling, zodat de lijst herlaadt. */
  onLinked: () => void;
}

/**
 * Een financieel document aan een **renovatieticket** koppelen, vanuit de
 * documentenlijst.
 *
 * De koppeling bestond al maar kon alleen vanaf het ticket gelegd worden. Wie in de
 * documentenlijst zit, wil ze daar kunnen leggen — dat is de plek waar je de factuur voor
 * je hebt.
 *
 * Dezelfde endpoints als het ticket gebruikt, dus dezelfde regels gelden: een document
 * hangt aan ten hoogste één ticket, en koppelen aan een tweede ticket wordt geweigerd met
 * uitleg in plaats van stil overgezet.
 */
export const LinkRenoTicketModal: React.FC<Props> = ({
  document,
  onClose,
  onLinked,
}) => {
  const { session } = useSession();
  const [search, setSearch] = useState("");
  const [tickets, setTickets] = useState<RenoTicketOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!document) setSearch("");
  }, [document]);

  useEffect(() => {
    if (!document || !session) return;
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const params = new URLSearchParams({ page: "1", limit: "25" });
        if (search.trim()) params.set("search", search.trim());
        const res = await fetchWithAuth(
          `${API_URL}/reno-helpdesk/tickets?${params}`,
          { method: "GET" },
          session,
        );
        if (!res.ok) throw new Error("Renovatietickets ophalen mislukt");
        const json = await res.json();
        setTickets(json?.tickets ?? []);
      } catch (error) {
        Sentry.captureException(error, {
          tags: { module: "renovation-documents", action: "searchRenoTickets" },
          extra: { documentId: document.id, search },
        });
        toast.error("Renovatietickets ophalen mislukt");
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [document, search, session]);

  const link = useCallback(
    async (ticketId: number) => {
      if (!document || !session) return;
      setIsSaving(true);
      try {
        const res = await fetchWithAuth(
          `${API_URL}/reno-helpdesk/tickets/${ticketId}/financial-documents`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ documentId: document.id }),
          },
          session,
        );
        if (!res.ok) {
          // De backend legt uit waarom (bv. al aan een ander ticket gekoppeld).
          const uitleg = await res.json().then((d) => d?.message).catch(() => undefined);
          throw new Error(uitleg || "Koppelen mislukt");
        }
        toast.success(`Gekoppeld aan RS-${ticketId}`);
        onLinked();
        onClose();
      } catch (error) {
        Sentry.captureException(error, {
          tags: { module: "renovation-documents", action: "linkRenoTicket" },
          extra: { documentId: document.id, ticketId },
        });
        toast.error((error as Error).message);
      } finally {
        setIsSaving(false);
      }
    },
    [document, session, onLinked, onClose],
  );

  const unlink = useCallback(async () => {
    if (!document?.renoTicketId || !session) return;
    setIsSaving(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/reno-helpdesk/tickets/${document.renoTicketId}/financial-documents/${document.id}`,
        { method: "DELETE" },
        session,
      );
      if (!res.ok) throw new Error("Koppeling losmaken mislukt");
      toast.success("Koppeling losgemaakt");
      onLinked();
      onClose();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { module: "renovation-documents", action: "unlinkRenoTicket" },
        extra: { documentId: document.id, ticketId: document.renoTicketId },
      });
      toast.error((error as Error).message);
    } finally {
      setIsSaving(false);
    }
  }, [document, session, onLinked, onClose]);

  return (
    <Dialog open={!!document} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Renovatieticket koppelen aan {document?.documentNumber}
          </DialogTitle>
          <DialogDescription>
            Het ticket leidt uit de gekoppelde documenten zijn goedgekeurd offertebedrag en
            factuurbedrag af. Eén document hangt aan ten hoogste één ticket.
          </DialogDescription>
        </DialogHeader>

        {document?.renoTicketId && (
          <div className="flex items-center justify-between rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2">
            <span className="text-neutral-primary text-sm">
              Nu gekoppeld aan <strong>RS-{document.renoTicketId}</strong>
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={isSaving}
              onClick={unlink}
              className="h-7 gap-1 text-xs"
            >
              <X size={12} />
              Losmaken
            </Button>
          </div>
        )}

        <div className="relative">
          <MagnifyingGlass
            size={14}
            className="text-neutral-secondary-mid absolute top-1/2 left-2.5 -translate-y-1/2"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Zoek op tickettitel, project, gebouw..."
            className="h-8 pl-8 text-xs"
          />
        </div>

        <div className="max-h-72 space-y-1 overflow-y-auto">
          {isSearching ? (
            <div className="text-neutral-secondary-mid flex items-center gap-2 px-1 py-3 text-xs">
              <Spinner className="h-3 w-3" /> Zoeken...
            </div>
          ) : tickets.length === 0 ? (
            <p className="text-neutral-secondary-mid px-1 py-3 text-xs">
              Geen renovatietickets gevonden.
            </p>
          ) : (
            tickets.map((ticket) => (
              <button
                key={ticket.id}
                onClick={() => link(ticket.id)}
                disabled={isSaving || ticket.id === document?.renoTicketId}
                className="hover:bg-neutral-primary/5 flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs disabled:opacity-50"
              >
                <Receipt size={13} weight="duotone" className="text-moss-primary shrink-0" />
                <span className="min-w-0 flex-1 truncate">
                  <span className="text-neutral-primary font-medium">RS-{ticket.id}</span>
                  <span className="text-neutral-primary"> {ticket.title}</span>
                  <span className="text-neutral-secondary-mid">
                    {" · "}
                    {ticket.buildingId ?? "geen gebouw"}
                    {ticket.renovationProject ? ` · ${ticket.renovationProject.title}` : ""}
                  </span>
                </span>
                <span className="text-neutral-secondary-mid shrink-0">{ticket.status}</span>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LinkRenoTicketModal;
